import { website } from '@getpopsure/private-constants';
import { Region } from '@getpopsure/public-models';
import dayjs from 'dayjs';
import { imageTypeMapping } from 'models/insurances/types/mapping';
import { TFunction } from 'shared/i18n/utils';
import { SignupQuestionnaireType } from 'SignupQuestionnaire';

import { createPostQuote } from '../actions';
import { AddDependents } from '../components/AddDependents';
import { Quote } from '../components/Quote';
import { Signature } from '../components/Signature/Signature';
import {
  ExpatLongTerm,
  ExpatLongTermGroupIds,
  genderMapping,
  occupationMapping,
  occupationReviewMapping,
} from '../models';

export const DATE_FORMAT = 'DD MMM YYYY';

export const ExpatLongTermComponents = {
  QUOTE_PAGE: Quote,
  ADD_DEPENDENTS: AddDependents,
  SIGNATURE: Signature,
} as const;

export type ExpatLongTermQuestionnaire = SignupQuestionnaireType<
  ExpatLongTerm,
  ExpatLongTermGroupIds,
  typeof ExpatLongTermComponents
>;

// Used to set the minimum year that can be chosen to 2025
const deltaTo2025 = dayjs().isBefore('2025-1-1') ? 1 : 0;

export const getTranslatedQuestionnaire = (
  t: TFunction,
  region: string,
  regionId: Region,
  continueFromAgeIsBetween65And70Blocker: () => void,
  continueFromEmployedOccupationBlocker: () => void,
  useNewCheckout?: boolean
): ExpatLongTermQuestionnaire => [
  {
    id: 'intro',
    required: true,
    type: 'CUSTOM_INTRO',
    props: {
      title: t(
        'expatLongTerm.qnr.preQuote.intro.title',
        'Hey! Let’s get you a quote'
      ),
      subTitle: t(
        'expatLongTerm.qnr.preQuote.intro.subTitle',
        'It takes less than a minute, no email needed'
      ),
      iconTitle: 'Long term expat health insurance',
    },
    screen: {
      continueButtonText: 'Get started',
    },
    groupId: 'signup',
  },
  {
    id: 'dateOfBirth',
    required: true,
    type: 'DATE',
    props: {
      yearRange: {
        min: { op: 'subtract', type: 'years', value: 100 },
        max: { op: 'add', type: 'years', value: 0 },
      },
    },
    screen: {
      question: t(
        'expatLongTerm.qnr.preQuote.dateOfBirth.title',
        'When were you born?  '
      ),
    },
    validations: [
      {
        op: 'dateDiff',
        variable: {
          type: 'day',
          value: 1,
        },
        msg: {
          type: 'Info',
          value: t(
            'expatLongTerm.qnr.dateOfBirth.validation.errorMessage',
            'You can’t select a birth date in the future. Please make sure the date of birth is correct.'
          ),
        },
      },
    ],
    groupId: 'preQuote',
  },
  {
    id: 'ageIsAbove70Blocker',
    type: 'BLOCKER',
    props: {
      title: t(
        'expatLongTerm.qnr.preQuote.ageIsAbove70Blocker.title',
        'Unfortunately, we can’t sign you up'
      ),
      iconType: 'SHIELD',
      description: t(
        'expatLongTerm.qnr.preQuote.ageIsAbove70Blocker.description',
        'Because you are over 70 years old, you aren’t eligible for long term expat health insurance. \n\nIf you have questions, please feel free to contact us.'
      ),
      buttonProps: [
        {
          type: 'href',
          href: website.support,
          caption: t(
            'expatLongTerm.qnr.preQuote.ageIsAbove70Blocker.button',
            'Get in touch'
          ),
        },
      ],
    },
    screen: {
      layout: 'Standalone',
    },
    groupId: 'preQuote',
  },
  {
    id: 'ageIsBetween65And70Blocker',
    type: 'BLOCKER',
    props: {
      title: t(
        'expatLongTerm.qnr.preQuote.ageIsBetween65And70Blocker.title',
        'Health screening needed'
      ),
      iconType: 'SHIELD',
      description: t(
        'expatLongTerm.qnr.preQuote.ageIsBetween65And70Blocker.description',
        'As you are above 64 years of age, you will need to complete a health screening questionnaire to be able to sign up for our long term expat health insurance.\n\nPlease get in touch with our insurance experts via the button below before continuing with your application.\n\nYou can read further about the application process in [our FAQ]({{ faq }}) here.',
        {
          faq:
            regionId === 'de'
              ? 'https://faq.feather-insurance.com/en/articles/257646-long-term-expat-health-over-65-years-of-age-application-process'
              : 'https://faq.eu.feather-insurance.com/en/articles/257646-long-term-expat-health-over-65-years-of-age-application-process',
        }
      ),
      buttonProps: [
        {
          type: 'button',
          onClick: continueFromAgeIsBetween65And70Blocker,
          caption: t(
            'expatLongTerm.qnr.preQuote.ageIsBetween65And70Blocker.button',
            'Continue application'
          ),
        },
        {
          type: 'href',
          href: website.support,
          variant: 'SECONDARY',
          caption: t(
            'expatLongTerm.qnr.preQuote.ageIsBetween65And70Blocker.supportButton',
            'Get in touch'
          ),
        },
      ],
    },
    screen: {
      layout: 'Standalone',
    },
    groupId: 'preQuote',
  },
  {
    id: 'occupation',
    required: true,
    type: 'RADIO',
    props: {
      mapValue: occupationMapping(t),
    },
    screen: {
      question: t('expatLongTerm.qnr.preQuote.occupation.title', {
        defaultValue: 'What’s your main occupation in {{region}}?',
        region,
      }),
    },
    groupId: 'preQuote',
  },
  {
    id: 'professionalAthleteOccupationBlocker',
    type: 'BLOCKER',
    props: {
      title: t(
        'expatLongTerm.qnr.preQuote.professionalAthleteOccupationBlocker.title',
        'Unfortunately, we can’t sign you up'
      ),
      iconType: 'SHIELD',
      description: t(
        'expatLongTerm.qnr.preQuote.professionalAthleteOccupationBlocker.description',
        'As a professional athlete, we are not able to cover you with our expat health insurance.\n\nIf you have questions, please feel free to contact us.'
      ),
      buttonProps: [
        {
          type: 'href',
          href: website.support,
          caption: t(
            'expatLongTerm.qnr.preQuote.professionalAthleteOccupationBlocker.button',
            'Get in touch'
          ),
        },
      ],
    },
    screen: {
      layout: 'Standalone',
    },
    groupId: 'preQuote',
  },
  {
    id: 'employedOccupationBlocker',
    type: 'BLOCKER',
    props: {
      title: t(
        'expatLongTerm.qnr.preQuote.employedOccupationBlocker.title',
        'Expat health insurance might not be right for you'
      ),
      iconType: 'SHIELD',
      description: t(
        'expatLongTerm.qnr.preQuote.employedOccupationBlocker.description',
        {
          defaultValue:
            'Expat health insurance is typically not accepted by employers in {{ region }}. It does not offer full health insurance coverage or cover pre-existing conditions. \n\nIf you have questions, please feel free to contact us.',
          region,
        }
      ),
      buttonProps: [
        {
          type: 'button',
          onClick: continueFromEmployedOccupationBlocker,
          caption: t(
            'expatLongTerm.qnr.preQuote.employedOccupationBlocker.button',
            'Continue application'
          ),
        },
        {
          type: 'href',
          href: website.support,
          variant: 'SECONDARY',
          caption: t(
            'expatLongTerm.qnr.preQuote.employedOccupationBlocker.supportButton',
            'Get in touch'
          ),
        },
      ],
    },
    screen: {
      layout: 'Standalone',
    },
    groupId: 'preQuote',
  },
  {
    id: 'occupationDescription',
    required: false,
    type: 'INPUT',
    props: {},
    screen: {
      question: t('expatLongTerm.qnr.preQuote.occupationDescription.title', {
        defaultValue: 'Please specify your occupation in {{region}}?',
        region,
      }),
    },
    groupId: 'preQuote',
  },
  {
    id: 'addFamilyMembers',
    required: false,
    type: 'ADD_DEPENDENTS',
    props: {},
    screen: {
      layout: 'Standalone',
    },
    groupId: 'preQuote',
  },
  {
    id: 'quote',
    required: true,
    type: 'QUOTE_PAGE',
    props: {},
    screen: {
      question: '',
      layout: 'Standalone',
    },
    groupId: 'preQuote',
  },
  {
    id: 'signupIntro',
    required: true,
    type: 'INTRO',
    props: {
      title: t(
        'expatLongTerm.qnr.signup.signupIntro.title',
        "Great! Let's sign you up"
      ),
      subTitle: t(
        'expatLongTerm.qnr.signup.signupIntro.subTitle',
        'Just 7 questions to your policy, it takes 1-2 minutes.'
      ),
    },
    screen: {
      continueButtonText: t(
        'expatLongTerm.qnr.signup.signupIntro.continueButtonText',
        'Get started'
      ),
    },
    groupId: 'signup',
  },
  {
    id: 'email',
    required: true,
    type: 'EMAIL',
    props: {
      verticalId: 'expatLongTerm',
    },
    screen: {
      layout: 'Standalone',
      description: t(
        'expatLongTerm.qnr.signup.postalCode.email.description',
        'This policy will be accessible in your Feather account.'
      ),
    },
    groupId: 'signup',
  },
  {
    id: 'name',
    required: true,
    type: 'NAME',
    props: {},
    screen: {
      question: t('expatLongTerm.qnr.signup.name.title', "What's your name?"),
    },
    groupId: 'signup',
  },
  {
    id: 'gender',
    required: true,
    type: 'RADIO',
    props: {
      mapValue: genderMapping(t),
    },
    screen: {
      question: t(
        'expatLongTerm.qnr.signup.gender.title',
        'What is your gender?'
      ),
    },
    groupId: 'signup',
  },
  {
    id: 'citizenship',
    required: true,
    type: 'COUNTRY_MULTI',
    props: {},
    screen: {
      question: t(
        'expatLongTerm.qnr.signup.citizenship.title',
        'Which countries do you hold a passport from?'
      ),
    },
    groupId: 'signup',
  },
  {
    id: 'lastPermanentResidency',
    required: true,
    type: 'COUNTRY_SINGLE',
    props: {},
    screen: {
      question: t('expatLongTerm.qnr.signup.lastPermanentResidency.title', {
        defaultValue:
          'What was your country of residence before arriving in {{ region }}?',
        region,
      }),
    },
    groupId: 'signup',
  },
  {
    id: 'address',
    required: true,
    type: 'ADDRESS',
    props: {
      countryCode: regionId,
    },
    screen: {
      question: t('expatLongTerm.qnr.signup.address.title', {
        defaultValue: 'What is your address in {{ region }}',
        region,
      }),
    },
    groupId: 'signup',
  },
  {
    id: 'startDate',
    required: true,
    type: 'DATE',
    props: {
      yearRange: {
        min: { op: 'add', type: 'years', value: deltaTo2025 },
        max: { op: 'add', type: 'years', value: 5 },
      },
    },
    screen: {
      question: t(
        'expatLongTerm.qnr.signup.startDate.title',
        'When would you like your coverage to start?'
      ),
    },
    validations: [
      {
        op: 'dateDiff',
        variable: {
          type: 'day',
          value: -60,
        },
        msg: {
          type: 'Info',
          value: t(
            'expatLongTerm.qnr.signup.startDate.errorMessage60DaysInFuture',
            'Coverage start date should not be more than 60 days in the future. Please adjust the start date.'
          ),
        },
      },
      {
        op: 'Custom',
        // date must be today or after
        fn: (answer: unknown) =>
          dayjs()
            .subtract(1, 'day')
            .isBefore(dayjs(answer as string)),
        msg: () => ({
          msg: t(
            'expatLongTerm.qnr.signup.startDate.dateIsInFuture',
            'Earliest start date is today. Please select another date.'
          ),
          type: 'Info',
        }),
      },
    ],
    groupId: 'signup',
  },
  {
    id: 'legalGuardianName',
    type: 'NAME',
    props: {},
    screen: {
      question: t(
        'expatLongTerm.qnr.signup.legalGuardianName.title',
        "What's the name of your parent or legal guardian?"
      ),
      description: t(
        'expatLongTerm.qnr.signup.legalGuardianName.description',
        "As you are under 18 years old, we need your parent / legal guardian's details to be able to sign you up."
      ),
    },
    groupId: 'signup',
  },
  {
    id: 'legalGuardianDateOfBirth',
    type: 'DATE',
    props: {
      yearRange: {
        min: { op: 'subtract', type: 'years', value: 100 },
        max: { op: 'subtract', type: 'years', value: 18 },
      },
    },
    screen: {
      question: t(
        'expatLongTerm.qnr.signup.legalGuardianDateOfBirth.title',
        'When were they born?'
      ),
    },
    validations: [
      {
        op: 'dateDiff',
        variable: {
          type: 'year',
          value: 18,
        },
        msg: {
          type: 'Info',
          value: t(
            'expatLongTerm.qnr.signup.legalGuardianDateOfBirth.errorMessage',
            'Legal guardian / parent should be over 18 years old.\n\nPlease make sure the birth date is correct.'
          ),
        },
      },
    ],
    groupId: 'signup',
  },
  {
    id: 'legalGuardianEmail',
    type: 'EMAIL_BASIC',
    props: {},
    screen: {
      question: t(
        'expatLongTerm.qnr.signup.legalGuardianEmail.email.title',
        "What's their email address?"
      ),
    },
    groupId: 'signup',
  },
  {
    id: 'signature',
    required: true,
    type: 'SIGNATURE',
    props: {},
    screen: {
      layout: 'Standalone',
    },
    groupId: 'signup',
  },
  {
    id: 'review',
    required: true,
    type: 'REVIEW',
    props: {
      requestType: 'CREATE_POST_QUOTE_SUBMIT_INFO',
      reviewValues: [
        {
          title: t('expatLongTerm.qnr.signup.review.plan.title', 'Plan'),
          value: {
            type: 'Strings',
            key: 'quote',
            keys: ['plan'],
          },
          path: 'quote',
        },
        {
          title: t('expatLongTerm.qnr.signup.review.email.title', 'Email'),
          value: { type: 'String', key: 'email' },
          path: 'email',
        },
        {
          title: t(
            'expatLongTerm.qnr.signup.review.name.title',
            "Policy holder's name"
          ),
          value: {
            type: 'Strings',
            key: 'name',
            keys: ['firstName', 'lastName'],
          },
          path: 'name',
        },
        {
          title: t(
            'expatLongTerm.qnr.signup.review.dateOfBirth.title',
            'Date of birth'
          ),
          value: { type: 'Date', format: 'DD MMM YYYY', key: 'dateOfBirth' },
          path: 'dateOfBirth',
        },
        {
          title: t('expatLongTerm.qnr.signup.review.gender.title', 'Gender'),
          value: {
            type: 'String',
            valueMap: genderMapping(t),
            key: 'gender',
          },
          path: 'gender',
        },
        {
          title: t(
            'expatLongTerm.qnr.signup.review.occupation.title',
            'Occupation'
          ),
          value: {
            type: 'String',
            valueMap: occupationReviewMapping(t),
            key: 'occupation',
          },
          path: 'occupation',
        },
        {
          title: t(
            'expatLongTerm.qnr.signup.review.addFamilyMembers.title',
            'Who to cover?'
          ),
          value: {
            type: 'Boolean',
            valueMap: {
              true: t(
                'expatLongTerm.qnr.signup.review.addFamilyMembers.valueMap.yes',
                'Myself and my family'
              ),
              false: t(
                'expatLongTerm.qnr.signup.review.addFamilyMembers.valueMap.no',
                'Only myself'
              ),
            },
            key: 'addFamilyMembers',
          },
          path: 'addFamilyMembers',
        },
        {
          title: t(
            'expatLongTerm.qnr.signup.review.citizenship.title',
            'Citizenship'
          ),
          value: { type: 'Array', pick: ['name'], key: 'citizenship' },
          path: 'citizenship',
        },
        {
          title: t(
            'expatLongTerm.qnr.signup.review.lastPermanentResidency.title',
            'Last permanent residency'
          ),
          value: {
            type: 'Strings',
            key: 'lastPermanentResidency',
            keys: ['name'],
          },
          path: 'lastPermanentResidency',
        },
        {
          title: t('expatLongTerm.qnr.signup.review.address.title', 'Address'),
          value: {
            type: 'Strings',
            key: 'address',
            keys: ['street', 'houseNumber', 'postcode', 'city'],
          },
          path: 'address',
        },
        {
          title: t(
            'expatLongTerm.qnr.signup.review.startDate.title',
            'Coverage start date'
          ),
          value: { type: 'Date', format: 'DD MMM YYYY', key: 'startDate' },
          path: 'startDate',
        },
        {
          title: t(
            'expatLongTerm.qnr.signup.review.legalGuardianName',
            "Parent / legal guardian's name"
          ),
          value: {
            type: 'Strings',
            key: 'legalGuardianName',
            keys: ['firstName', 'lastName'],
          },
          path: 'legalGuardianName',
        },
        {
          title: t(
            'expatLongTerm.qnr.signup.review.legalGuardianDateOfBirth',
            "Parent / legal guardian's date of birth"
          ),
          value: {
            type: 'Date',
            key: 'legalGuardianDateOfBirth',
            format: 'DD MMM YYYY',
          },
          path: 'legalGuardianDateOfBirth',
        },
        {
          title: t(
            'expatLongTerm.qnr.signup.review.legalGuardianEmail',
            "Parent / legal guardian's email"
          ),
          value: { type: 'String', key: 'legalGuardianEmail' },
          path: 'legalGuardianEmail',
        },
      ],
      verticalId: 'expatLongTerm',
      insuranceType: 'INCOMING_LT',
      region: regionId,
      createPostQuote: () => createPostQuote(t),
      confirmationText: t(
        'expatLongTerm.qnr.signup.review.confirmation',
        'By selecting "Continue", I confirm to have answered all questions truthfully. Knowingly omitting any relevant details entitles the insurer to cancel the contract—either retroactively or from the date the omission is discovered.'
      ),
    },
    screen: {
      layout: 'Standalone',
    },
    groupId: 'signup',
  },
  ...(useNewCheckout
    ? [
        {
          id: 'reviewCheckout' as const,
          required: true,
          type: 'GENERIC_CHECKOUT_V2' as const,
          props: {
            type: 'INCOMING_LT' as const,
            productName: t(
              'expatLongTerm.qnr.checkout.productName',
              'Expat health'
            ),
            planQuestionId: 'plan',
            region: regionId,
            policyInfoKind: 'ExpatLTPolicy',
            hasCheckoutDocuments: true,
            redirectAddDependent: 'addFamilyMembers',
            policyDetailsConfiguration: {
              productCardDetails: [
                {
                  id: 'startDate',
                  label: t(
                    'expatLongTerm.qnr.checkoutV2.productCard.startDate',
                    'Start date'
                  ),
                  questionId: 'startDate',
                },
                {
                  id: 'region',
                  label: t(
                    'expatLongTerm.qnr.checkout.checkoutV2.countryOfInsurance',
                    'Insured in'
                  ),
                  questionId: 'region',
                  dataType: 'REGION' as const,
                },
                {
                  id: 'includesUSAndCaribbean',
                  label: t(
                    'expatLongTerm.qnr.checkoutV2.productCard.hasUSCaribbeanCoverage',
                    'US & Caribbean add-on'
                  ),
                  questionId: 'includesUSAndCaribbean',
                },
              ],
              policyDetails: [
                {
                  id: 'name',
                  label: t(
                    'expatLongTerm.qnr.checkoutV2.policyDetails.name',
                    'Name'
                  ),
                  questionId: 'name',
                },
                {
                  id: 'dateOfBirth',
                  label: t(
                    'expatLongTerm.qnr.checkoutV2.policyDetails.dateOfBirth',
                    'Date of Birth'
                  ),
                  questionId: 'dateOfBirth',
                },
                {
                  id: 'email',
                  label: t(
                    'expatLongTerm.qnr.checkoutV2.policyDetails.email',
                    'Account email'
                  ),
                  questionId: 'email',
                },
              ],
              additionalPolicyDetails: [
                {
                  id: 'legalGuardian',
                  title: t(
                    'expatLongTerm.qnr.checkoutV2.policyDetails.legalGuardian.subTitle',
                    'Legal guardian'
                  ),
                  details: [
                    {
                      id: 'legalGuardianName',
                      label: t(
                        'expatLongTerm.qnr.checkoutV2.policyDetails.legalGuardian.name',
                        'Name'
                      ),
                      questionId: 'legalGuardianName',
                    },
                    {
                      id: 'legalGuardianDateOfBirth',
                      label: t(
                        'expatLongTerm.qnr.checkoutV2.policyDetails.legalGuardian.dateOfBirth',
                        'Date of Birth'
                      ),
                      questionId: 'legalGuardianDateOfBirth',
                    },
                    {
                      id: 'legalGuardianEmail',
                      label: t(
                        'expatLongTerm.qnr.checkoutV2.policyDetails.legalGuardian.email',
                        'Email'
                      ),
                      questionId: 'legalGuardianEmail',
                    },
                  ],
                },
              ],
            },
          },
          screen: {
            layout: 'Standalone' as const,
          },
          groupId: 'signup' as const,
        },
      ]
    : [
        {
          id: 'reviewCheckout' as const,
          required: true,
          type: 'GENERIC_CHECKOUT' as const,
          props: {
            type: 'INCOMING_LT' as const,
            icon: imageTypeMapping.INCOMING_LT,
            region: regionId,
            policyInfoKind: 'ExpatLTPolicy',
            hasCheckoutDocuments: true,
            redirectAddDependent: 'addFamilyMembers',
            policyDetailsConfiguration: {
              policyDetails: [
                {
                  title: t(
                    'expatLongTerm.qnr.checkout.policyDetails.name',
                    'Name'
                  ),
                  questionId: 'name',
                },
                {
                  title: t(
                    'expatLongTerm.qnr.checkout.policydetails.requestedStartDate',
                    'Requested start date'
                  ),
                  questionId: 'startDate',
                  additionalInfo: {
                    title: t(
                      'expatLongTerm.qnr.checkout.policydetails.requestedStartDate.additionalInfo.title',
                      'Requested start date'
                    ),
                    description: t(
                      'expatLongTerm.qnr.checkout.policydetails.requestedStartDate.additionalInfo.descriptionNoValue',
                      'Your coverage should begin on the requested date, but only after you have received the official confirmation.'
                    ),
                  },
                },
                {
                  title: t(
                    'expatLongTerm.qnr.checkout.policyDetails.countryOfInsurance',
                    'Country of insurance'
                  ),
                  questionId: 'region',
                  dataType: 'REGION' as const,
                },
                {
                  title: t(
                    'expatLongTerm.qnr.checkout.policyDetails.plan',
                    'Plan'
                  ),
                  questionId: 'plan',
                },
                {
                  title: t(
                    'expatLongTerm.qnr.checkout.policyDetails.deductible',
                    'Out-of-pocket amount'
                  ),
                  questionId: 'deductible',
                  dataType: 'CURRENCY' as const,
                },
                {
                  title: t(
                    'expatLongTerm.qnr.checkout.policyDetails.hasUSCaribbeanCoverage',
                    'Coverage for the US and Caribbean'
                  ),
                  questionId: 'includesUSAndCaribbean',
                },
                {
                  title: t(
                    'expatLongTerm.qnr.checkout.policyDetails.dateOfBirth',
                    'Date of Birth'
                  ),
                  questionId: 'dateOfBirth',
                },
                {
                  title: t(
                    'expatLongTerm.qnr.checkout.policyDetails.email',
                    'Email'
                  ),
                  questionId: 'email',
                },
              ],
              otherSections: [
                {
                  title: t(
                    'expatLongTerm.qnr.checkout.policyDetails.legalGuardian.subTitle',
                    'Parent / Legal guardian'
                  ),
                  rows: [
                    {
                      title: t(
                        'expatLongTerm.qnr.checkout.policyDetails.legalGuardian.name',
                        'Name'
                      ),
                      questionId: 'legalGuardianName',
                    },
                    {
                      title: t(
                        'expatLongTerm.qnr.checkout.policyDetails.legalGuardian.dateOfBirth',
                        'Date of Birth'
                      ),
                      questionId: 'legalGuardianDateOfBirth',
                    },
                    {
                      title: t(
                        'expatLongTerm.qnr.checkout.policyDetails.legalGuardian.email',
                        'Email'
                      ),
                      questionId: 'legalGuardianEmail',
                    },
                  ],
                },
              ],
            },
          },
          screen: {
            layout: 'Standalone' as const,
          },
          groupId: 'signup' as const,
        },
      ]),
];
